import { SimilarSearchLocationCard } from '@/utils/buildExploreSimilarSearchLocationCards';
import cx from 'classnames';
import Link from 'next/link';
import { useState } from 'react';
import { pushGtmEvent } from 'utils/gtm';

import styles from './ExploreSimilarSearches.module.scss';

type SimilarSearchTab = 'location' | 'type';

export interface ExploreSimilarSearchesProps {
  locationCards: SimilarSearchLocationCard[];
  typeCards: SimilarSearchLocationCard[];
}

export const ExploreSimilarSearches = ({
  locationCards,
  typeCards,
}: ExploreSimilarSearchesProps) => {
  const hasLocationCards = locationCards.length > 0;
  const hasTypeCards = typeCards.length > 0;
  const [selectedTab, setSelectedTab] = useState<SimilarSearchTab>('location');
  // An empty tab is never offered at all (A2P-4475), so fall back to whichever tab has cards -
  // derived each render rather than only at mount, since a client-side route change can swap in
  // new cards (and empty the selected tab) without remounting this component.
  const isSelectedTabEmpty = selectedTab === 'location' ? !hasLocationCards : !hasTypeCards;
  const otherTab: SimilarSearchTab = selectedTab === 'location' ? 'type' : 'location';
  const activeTab = isSelectedTabEmpty ? otherTab : selectedTab;

  if (!hasLocationCards && !hasTypeCards) {
    return null;
  }

  const handleTabClick = (tab: SimilarSearchTab) => {
    if (tab === activeTab) {
      return;
    }
    setSelectedTab(tab);
    pushGtmEvent({
      event: 'similar_search_tab',
      element: 'Explore Similar Searches Tab',
      event_label: tab === 'location' ? 'by_location' : 'by_type',
    });
  };

  const handleCardClick = (card: SimilarSearchLocationCard) => {
    pushGtmEvent({
      event: 'similar_search_click',
      element: 'Explore Similar Searches Card',
      link_text: card.label,
      link_url: card.href,
    });
  };

  const renderPanel = (tab: SimilarSearchTab, cards: SimilarSearchLocationCard[]) => (
    <div
      role="tabpanel"
      id={`explore-similar-searches-panel-${tab}`}
      aria-labelledby={`explore-similar-searches-tab-${tab}`}
      aria-hidden={activeTab !== tab}
      className={cx(styles.panel, { [styles.panelHidden]: activeTab !== tab })}
      data-testid={`explore-similar-searches-panel-${tab}`}
    >
      {cards.map(card => (
        // href is the relative canonical path so next/link's own client-side routing
        // (which normalizes/matches same-origin hrefs against a dummy base URL - see
        // resolveHref in next/dist/client/resolve-href.js) works correctly; `as` renders
        // the absolute canonical URL in the DOM and browser address bar for SEO/crawlability
        // without going through that same-origin normalization.
        <Link
          key={card.id}
          href={card.path}
          as={card.href}
          className={styles.card}
          onClick={() => handleCardClick(card)}
        >
          <span className={styles.cardLabel}>{card.label}</span>
          <span className={styles.cardCount}>{card.count}</span>
        </Link>
      ))}
    </div>
  );

  return (
    <div className={styles.exploreSimilarSearches} data-testid="explore-similar-searches">
      <div className={styles.header}>
        <h2 className={styles.heading}>
          <span className={styles.accent}>Explore</span> similar searches
        </h2>
        <p className={styles.description}>
          Browse related specialties, locations and job types to find more opportunities that match
          what you're looking for.
        </p>
      </div>

      <div className={styles.body}>
        <div className={styles.tabBar} role="tablist" aria-label="Explore similar searches">
          {hasLocationCards && (
            <button
              type="button"
              role="tab"
              id="explore-similar-searches-tab-location"
              aria-selected={activeTab === 'location'}
              aria-controls="explore-similar-searches-panel-location"
              className={cx(styles.tabButton, {
                [styles.tabButtonActive]: activeTab === 'location',
              })}
              onClick={() => handleTabClick('location')}
            >
              By location
            </button>
          )}
          {hasTypeCards && (
            <button
              type="button"
              role="tab"
              id="explore-similar-searches-tab-type"
              aria-selected={activeTab === 'type'}
              aria-controls="explore-similar-searches-panel-type"
              className={cx(styles.tabButton, { [styles.tabButtonActive]: activeTab === 'type' })}
              onClick={() => handleTabClick('type')}
            >
              By type
            </button>
          )}
        </div>

        <div className={styles.cardGrid}>
          {hasLocationCards && renderPanel('location', locationCards)}
          {hasTypeCards && renderPanel('type', typeCards)}
        </div>
      </div>
    </div>
  );
};

export default ExploreSimilarSearches;
